<template>
  <div id="traffic-routes-layer"></div>
</template>

<script>
/**
 * マップ上に航路を表示するコンポーネント
 * @parent O.MapViewGridItem
 * @props map: Object １マップ分のインスタンスデータ
 * @props isReady: Boolean マップ情報が描画されているかのフラグ
 * @props mapEvent: String マップ上操作でのイベント名
 */
import { createNamespacedHelpers } from "vuex";
import { NavigationsMixin } from "@/mixins";

// Store
const { mapActions: mapActionsOfLiveMap } = createNamespacedHelpers("liveMap");

export default {
  name: "O.TrafficRoutesLayer",
  mixins: [NavigationsMixin],
  data() {
    return {
      beforeLayerId: "PlotTextLayer",
      layersMinZoom: 5,
      layersMinZoomForText: 10,
      maritimeTrafficColors: {
        base: "#00BBF5",
        text: "#00BBF5",
      },
      portTrafficColors: {
        base: "#B14FFF",
        text: "#B14FFF",
      },
      trafficRoutesSourceId: "trafficRoutesSource",
      maritimeTrafficRegulationsFillLayerId:
        "maritimeTrafficRegulationsFillLayer",
      maritimeTrafficRegulationsLineLayerId:
        "maritimeTrafficRegulationsLineLayer",
      maritimeTrafficRegulationsSymbolLayerId:
        "maritimeTrafficRegulationsSymbolLayer",
      portTrafficRegulationsFillLayerId: "portTrafficRegulationsFillLayer",
      portTrafficRegulationsLineLayerId: "portTrafficRegulationsLineLayer",
      portTrafficRegulationSymbolLayerId: "portTrafficRegulationsSymbolLayer",
    };
  },
  props: {
    map: Object,
    isReady: { type: Boolean, default: false },
    mapEvent: String,
    mapId: {
      type: String,
      required: false,
      default: "",
    },
    gridMapSetting: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    filterNavigations() {
      if (!this.isReady) return [];
      const { gridMapSetting } = this;
      if (!gridMapSetting) return [];
      return gridMapSetting.filter_navigations || [];
    },
  },
  watch: {
    async isReady() {
      if (this.isReady) {
        await this.setTrafficRoutesSource();
        await this.setTrafficRoutesLayers();
        this.setVisibility();
      }
    },
    filterNavigations() {
      if (this.isReady) {
        this.setVisibility();
      }
    },
  },
  methods: {
    ...mapActionsOfLiveMap([]),
    setVisibility() {
      if (
        this.filterNavigations.find(
          (el) =>
            el ===
            String(this.navigationsCategory.MARITIME_TRAFFIC_REGULATIONS_ROUTE),
        )
      ) {
        if (this.map.getLayer(this.maritimeTrafficRegulationsFillLayerId)) {
          this.map.setLayoutProperty(
            this.maritimeTrafficRegulationsFillLayerId,
            "visibility",
            "visible",
          );
        }
        if (this.map.getLayer(this.maritimeTrafficRegulationsLineLayerId)) {
          this.map.setLayoutProperty(
            this.maritimeTrafficRegulationsLineLayerId,
            "visibility",
            "visible",
          );
        }
        if (this.map.getLayer(this.maritimeTrafficRegulationsSymbolLayerId)) {
          this.map.setLayoutProperty(
            this.maritimeTrafficRegulationsSymbolLayerId,
            "visibility",
            "visible",
          );
        }
      } else {
        if (this.map.getLayer(this.maritimeTrafficRegulationsFillLayerId)) {
          this.map.setLayoutProperty(
            this.maritimeTrafficRegulationsFillLayerId,
            "visibility",
            "none",
          );
        }
        if (this.map.getLayer(this.maritimeTrafficRegulationsLineLayerId)) {
          this.map.setLayoutProperty(
            this.maritimeTrafficRegulationsLineLayerId,
            "visibility",
            "none",
          );
        }
        if (this.map.getLayer(this.maritimeTrafficRegulationsSymbolLayerId)) {
          this.map.setLayoutProperty(
            this.maritimeTrafficRegulationsSymbolLayerId,
            "visibility",
            "none",
          );
        }
      }
      if (
        this.filterNavigations.find(
          (el) =>
            el ===
            String(this.navigationsCategory.PORT_TRAFFIC_REGULATIONS_ROUTE),
        )
      ) {
        if (this.map.getLayer(this.portTrafficRegulationsFillLayerId)) {
          this.map.setLayoutProperty(
            this.portTrafficRegulationsFillLayerId,
            "visibility",
            "visible",
          );
        }
        if (this.map.getLayer(this.portTrafficRegulationsLineLayerId)) {
          this.map.setLayoutProperty(
            this.portTrafficRegulationsLineLayerId,
            "visibility",
            "visible",
          );
        }
        if (this.map.getLayer(this.portTrafficRegulationSymbolLayerId)) {
          this.map.setLayoutProperty(
            this.portTrafficRegulationSymbolLayerId,
            "visibility",
            "visible",
          );
        }
      } else {
        if (this.map.getLayer(this.portTrafficRegulationsFillLayerId)) {
          this.map.setLayoutProperty(
            this.portTrafficRegulationsFillLayerId,
            "visibility",
            "none",
          );
        }
        if (this.map.getLayer(this.portTrafficRegulationsLineLayerId)) {
          this.map.setLayoutProperty(
            this.portTrafficRegulationsLineLayerId,
            "visibility",
            "none",
          );
        }
        if (this.map.getLayer(this.portTrafficRegulationSymbolLayerId)) {
          this.map.setLayoutProperty(
            this.portTrafficRegulationSymbolLayerId,
            "visibility",
            "none",
          );
        }
      }
    },
    async setTrafficRoutesSource() {
      this.map.addSource(this.trafficRoutesSourceId, {
        type: "vector",
        url: "mapbox://aidea.traffic_routes",
      });
    },
    async setTrafficRoutesLayers() {
      // 海上交通安全法の航路
      this.map.addLayer(
        {
          id: this.maritimeTrafficRegulationsFillLayerId,
          type: "fill",
          minzoom: this.layersMinZoom,
          source: this.trafficRoutesSourceId,
          "source-layer": "maritime_traffic_regulations",
          layout: {
            visibility: "none",
          },
          paint: {
            "fill-color": this.maritimeTrafficColors.base,
            "fill-opacity": 0.3,
            "fill-antialias": false,
          },
        },
        this.beforeLayerId,
      );

      this.map.addLayer(
        {
          id: this.maritimeTrafficRegulationsLineLayerId,
          type: "line",
          minzoom: this.layersMinZoom,
          source: this.trafficRoutesSourceId,
          "source-layer": "maritime_traffic_regulations",
          layout: {
            visibility: "none",
          },
          paint: {
            "line-color": this.maritimeTrafficColors.base,
            "line-width": 1.0,
          },
        },
        this.beforeLayerId,
      );

      this.map.addLayer(
        {
          id: this.maritimeTrafficRegulationsSymbolLayerId,
          type: "symbol",
          minzoom: this.layersMinZoomForText,
          source: this.trafficRoutesSourceId,
          "source-layer": "maritime_traffic_regulations",
          layout: {
            "text-field": "{name_jp}",
            "text-size": 16,
            "text-allow-overlap": true,
            visibility: "none",
          },
          paint: {
            "text-color": this.maritimeTrafficColors.text,
            "text-halo-color": "#000000",
            "text-halo-width": 1,
          },
        },
        this.beforeLayerId,
      );

      // 港則法の航路
      this.map.addLayer(
        {
          id: this.portTrafficRegulationsFillLayerId,
          type: "fill",
          minzoom: this.layersMinZoom,
          source: this.trafficRoutesSourceId,
          "source-layer": "port_traffic_regulations",
          layout: {
            visibility: "none",
          },
          paint: {
            "fill-color": this.portTrafficColors.base,
            "fill-opacity": 0.3,
          },
        },
        this.beforeLayerId,
      );

      this.map.addLayer(
        {
          id: this.portTrafficRegulationsLineLayerId,
          type: "line",
          minzoom: this.layersMinZoom,
          source: this.trafficRoutesSourceId,
          "source-layer": "port_traffic_regulations",
          layout: {
            visibility: "none",
          },
          paint: {
            "line-color": this.portTrafficColors.base,
            "line-width": 1.0,
          },
        },
        this.beforeLayerId,
      );

      this.map.addLayer(
        {
          id: this.portTrafficRegulationSymbolLayerId,
          type: "symbol",
          minzoom: this.layersMinZoomForText,
          source: this.trafficRoutesSourceId,
          "source-layer": "port_traffic_regulations",
          layout: {
            "text-field": "{name_jp}",
            "text-size": 16,
            "text-allow-overlap": true,
            visibility: "none",
          },
          paint: {
            "text-color": this.portTrafficColors.text,
            "text-halo-color": "#000000",
            "text-halo-width": 1,
          },
        },
        this.beforeLayerId,
      );
    },
  },
};
</script>

<style lang="scss"></style>
